import { MaterialDesignContent, SnackbarProvider } from 'notistack';

import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './components/ThemeProvider/ThemeProvider';
import { StyledEngineProvider, Typography, styled } from '@mui/material';
import { QueryClientProvider } from 'react-query';
import { queryClient } from './api/queryClient';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

// import TemplatesPage from './pages/TemplatesPage';
import LastOperationsPage from 'app/pages/LastOperationsPage';
// import NewTemplatePage from 'app/pages/TemplatesPage/NewTemplatePage';
// import EditTemplate from 'app/pages/TemplatesPage/EditTemplate';
// import SignedOperationsPage from './pages/SignedOperationsPage';
import { LayoutProvider } from 'app/storage/LayoutContext';
import { lazy, Suspense, useEffect } from 'react';
import ROUTES from './constants/routes';
import Loader from './components/Loader';
import AniPayEditTemplate from './pages/anipay/edit-template/page';
import { StateFormProvider } from './pages/government-payment-portal/provider/StateFormProvider';
import { renderTemplate } from './utils/render';

// import CardOrderPage from 'app/pages/CardOrderPage';
// import SalaryPage from './pages/SalaryPage';
// import EmployeeAddPage from 'app/pages/EmployeeAddPage';

const AccountsCardsPage = lazy(() => import('./pages/AccountsCardsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage/NotFoundPage'));
const InProgressPage = lazy(() => import('app/pages/InProgressPage'));
const NotHasAccess = lazy(() => import('app/pages/NotHasAccess/NotHasAccess'));
const EmployeeAddPage = lazy(() => import('app/pages/EmployeeAddPage'));
const TemplatesPage = lazy(() => import('app/pages/TemplatesPage'));
const SalaryPage = lazy(() => import('app/pages/SalaryPage'));
const Login = lazy(() => import('./pages/Authorization/Login'));
const AuthHeader = lazy(() => import('./components/Layouts/AuthHeader'));
const Recovery = lazy(() => import('./pages/Authorization/Recovery'));
const EmailSent = lazy(() => import('./pages/Authorization/EmailSent'));
const NewPass = lazy(() => import('./pages/Authorization/NewPass'));
const ConfirmAsanImza = lazy(() => import('app/pages/Authorization/ConfirmAsanImza'));
const OTP = lazy(() => import('./pages/Authorization/OTP'));
const SelectCompany = lazy(() => import('./pages/Authorization/SelectCompany'));
const DocSign = lazy(() => import('./pages/Authorization/DocSign'));
const LinkConfirm = lazy(() => import('./pages/Authorization/LinkConfirm'));
const MainLayout = lazy(() => import('./components/Layouts/MainLayout'));
const HomePage = lazy(() => import('./pages/HomePage'));
const TransferPage = lazy(() => import('./pages/TransferPage'));
const PaymentOrdersPage = lazy(() => import('./pages/PaymentOrdersPage'));
const SignedOperationsPage = lazy(() => import('./pages/SignedOperationsPage'));
// const CreditsPage = lazy(() => import('./pages/CreditsPage'));

const OldProfile = lazy(() => import('./pages/ClientProfilePage'));

// User profile
const ProfileRoot = lazy(() => import('./pages/profile/ui/layout'));
const CheckUserLayout = lazy(() => import('./pages/profile/ui/check-user-layout'));
const MainPage = lazy(() => import('./pages/profile/ui/info/page'));
const Users = lazy(() => import('./pages/profile/ui/users/page'));
const CreateUser = lazy(() => import('./pages/profile/ui/create-user/page'));
const UpdateUser = lazy(() => import('./pages/profile/ui/update-user/page'));

// Guarantee page
const GuaranteeOrder = lazy(() => import('./pages/guarantee-order/ui/page'));
const GuaranteeList = lazy(() => import('./pages/guarantee-list/ui/page'));
const GuaranteeListIncomings = lazy(() => import('./pages/guarantee-list/ui/guarantee-list.incomings'));
const GuaranteeListOutgoings = lazy(() => import('./pages/guarantee-list/ui/guarantee-list.outgoings'));

//AniPay page
const AniPayRoot = lazy(() => import('./pages/anipay/ui/layout'));
const AniPayPage = lazy(() => import('./pages/anipay/ui/page'));
const AniPaySignUp = lazy(() => import('app/pages/anipay/sign-up/ui/page'));
const AniPayAliases = lazy(() => import('app/pages/anipay/alias/ui/page'));
const AniPayNewPayment = lazy(() => import('app/pages/anipay/new-payment/ui/page'));

//Login layout
const LoginWrapper = lazy(() => import('./components/Layouts/login-wrapper/ui/login-wrapper'));
// const AuthWrapper = lazy(() => import('./components/Layouts/AuthHeader/AuthHeader'));
// Government Payment Portal page
const GovernmentPaymentPortalPage = lazy(() => import('./pages/government-payment-portal/ui/page'));

const GovernmentCustomsCommitteePage = lazy(
  () => import('./pages/government-payment-portal/ui/customs-committee/page')
);
const CustomsComitteeSummaryPage = lazy(
  () => import('./pages/government-payment-portal/ui/summary/CustomCommitteeSummary')
);

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-error': {
    backgroundColor: '#FF5F57',
  },
}));

const App = () => {
  useEffect(() => {
    // let lastTouchEnd = 0;
    window.addEventListener('keydown', function (e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=')) {
        e.preventDefault();
      }
    });
  }, []);

  return (
    <Suspense
      fallback={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
          }}
        >
          <Loader />
          <br />
          <Typography mt={2}>Məlumatlarınız yüklənir</Typography>
        </div>
      }
    >
      <ErrorBoundary>
        <ThemeProvider mode={'light'}>
          <SnackbarProvider
            Components={{
              error: StyledMaterialDesignContent,
            }}
          />
          <StyledEngineProvider injectFirst>
            <QueryClientProvider client={queryClient}>
              <LayoutProvider>
                <BrowserRouter>
                  <Routes>
                    <Route path={'/'} element={<Navigate to={'az/login'} replace />} />
                    <Route element={<LoginWrapper />}>
                      <Route path={ROUTES.login} element={<Login />} />
                      <Route path={ROUTES.recovery} element={<Recovery />} />
                      <Route path={ROUTES.linkConfirm} element={<LinkConfirm />} />
                      <Route path={ROUTES.linkSent} element={<EmailSent />} />
                      <Route path={ROUTES.setNewPass} element={<NewPass />} />
                      <Route path={ROUTES.confirmAsanImza} element={<ConfirmAsanImza />} />
                      <Route path={ROUTES.otp} element={<OTP />} />
                      <Route path={ROUTES.selectCompany} element={<SelectCompany />} />
                      <Route path={ROUTES.docSign} element={<DocSign />} />
                    </Route>
                    <Route element={<MainLayout />}>
                      <Route path={ROUTES.homePage} element={<HomePage />} />
                      <Route path={ROUTES.transferPage} element={<TransferPage />} />
                      <Route path={ROUTES.paymentOrdersPage} element={<PaymentOrdersPage />} />
                      <Route path="/az/old-profile" element={<OldProfile />} />
                      <Route path={ROUTES.clientProfile} element={<CheckUserLayout />}>
                        <Route element={<ProfileRoot />}>
                          <Route path="info" element={<MainPage />} />
                          <Route path="users" element={<Users />} />
                        </Route>
                        <Route path="create-user" element={<CreateUser />} />
                        <Route path="update-user/:employeeId" element={<UpdateUser />} />
                        <Route index element={<Navigate to="info" />} />
                      </Route>
                      <Route path={ROUTES.lastOperations} element={<LastOperationsPage />} />
                      <Route
                        path={ROUTES.guaranteeOrderPage}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Outlet />
                          </Suspense>
                        }
                      >
                        <Route index element={<Navigate to={'order'} replace />} />
                        <Route path={'order'} element={<GuaranteeOrder />} />
                        <Route path={'list'} element={<GuaranteeList />}>
                          <Route index element={<Navigate to={'incomings'} replace />} />
                          <Route path={'incomings'} element={<GuaranteeListIncomings />} />
                          <Route path={'outgoings'} element={<GuaranteeListOutgoings />} />
                        </Route>
                      </Route>
                      <Route path={ROUTES.accountsCards} element={<AccountsCardsPage />} />
                      <Route path={ROUTES.templatesPage} element={<TemplatesPage />} />
                      <Route path={ROUTES.newTemplate} element={<TransferPage />} />
                      <Route path={ROUTES.editTemplate} element={<TransferPage />} />
                      <Route path={ROUTES.signedOperations} element={<SignedOperationsPage />} />
                      {/* <Route path={ROUTES.cardOrder} element={<CardOrderPage />} /> */}
                      <Route path={ROUTES.salaryPage} element={<SalaryPage />} />
                      <Route path={ROUTES.employeeAdd} element={<EmployeeAddPage />} />
                      {/* <Route path={ROUTES.creditsPage} element={<CreditsPage />} /> */}
                      <Route path={ROUTES.aniPay}>
                        <Route element={<AniPayRoot />}>
                          <Route index element={<AniPayPage />} />
                          <Route path={'sign-up'} element={<AniPaySignUp />} />
                          <Route path={'alias/:dataType'} element={<AniPayAliases />} />
                          <Route path={'new-payment'} element={<AniPayNewPayment />} />
                          <Route path={'edit-template/:templateId'} element={<AniPayEditTemplate />} />
                        </Route>
                        <Route />
                      </Route>
                      <Route path={ROUTES.governmentPaymentPortal}>
                        <Route
                          element={
                            <StateFormProvider>
                              <Outlet />
                            </StateFormProvider>
                          }
                        >
                          <Route index element={<GovernmentPaymentPortalPage />} />
                          <Route path={'dgk'} element={<GovernmentCustomsCommitteePage />} />
                          <Route path={'dgk/summary'} element={<CustomsComitteeSummaryPage />} />
                        </Route>
                        <Route />
                      </Route>
                    </Route>
                    <Route path={ROUTES.notHasAccess} element={<NotHasAccess />} />
                    <Route path={'*'} element={<NotFoundPage />}></Route>
                    {[
                      // ROUTES.creditsPage,
                      // ROUTES.clientProfile,
                      // ROUTES.employeeAdd,
                      // ROUTES.signedOperations,
                      // ROUTES.newTemplate,
                      // ROUTES.editTemplate,
                      // ROUTES.templatesPage,
                      ROUTES.cardOrder,
                      // ROUTES.salaryPage,
                      ROUTES.depositsPage,
                      ROUTES.ordersPage,
                      // ROUTES.lastOperations,
                      // ROUTES.clientProfile,
                    ].map((path, index) => (
                      <Route path={path} element={<InProgressPage />} key={index} />
                    ))}
                  </Routes>
                </BrowserRouter>
              </LayoutProvider>
            </QueryClientProvider>
          </StyledEngineProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </Suspense>
  );
};

export default App;
